import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useSiteContent } from "@/lib/siteContent";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1724500123062-ca4c00df91e6?w=1800&q=85";

function backendPrefix(url) {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  if (url.startsWith("/api/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function Experiences() {
  const { lang } = useI18n();
  const { stays } = useSiteContent();
  const sorted = [...(stays || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div>
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-8">
        <div className="overline text-[#D4AF37]">{lang === "fr" ? "Signatures" : "Signatures"}</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl text-[#0A1128] mt-4 leading-none">
          {lang === "fr" ? "Nos séjours." : "Our stays."}
        </h1>
      </section>

      {sorted.length === 0 && (
        <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
          <p className="text-[#0A1128]/60 italic">
            {lang === "fr" ? "Les séjours apparaîtront ici dès qu'ils seront ajoutés depuis l'espace admin." : "Stays will appear here once added from the admin space."}
          </p>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 space-y-32">
        {sorted.map((s, idx) => {
          const primary = (s.images || []).find((i) => i.is_primary) || (s.images || [])[0];
          const imgUrl = backendPrefix(primary?.url) || FALLBACK_IMG;
          const name = lang === "fr" ? s.name_fr : (s.name_en || s.name_fr);
          const desc = lang === "fr" ? s.description_fr : (s.description_en || s.description_fr);
          const tiers = ["essential", "signature", "prestige"].map((k) => s[k]).filter(Boolean);
          return (
            <section id={s.slug} key={s.stay_id} className="grid md:grid-cols-12 gap-10" data-testid={`stay-${s.slug}`}>
              <div className={`md:col-span-7 hover-scale overflow-hidden ${idx % 2 === 1 ? "md:order-2" : ""}`}>
                <img src={imgUrl} alt={name} className="w-full h-[520px] object-cover" />
              </div>
              <div className={`md:col-span-5 ${idx % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="overline text-[#D4AF37]">{s.region}</div>
                <h2 className="font-serif text-4xl md:text-5xl font-light text-[#0A1128] mt-3">{name}</h2>
                {desc && <p className="mt-6 text-[#0A1128]/75 leading-relaxed">{desc}</p>}
                <div className="mt-8 space-y-6">
                  {tiers.map((t, i) => {
                    const title = lang === "fr" ? t.title_fr : (t.title_en || t.title_fr);
                    const tdesc = lang === "fr" ? t.description_fr : (t.description_en || t.description_fr);
                    const incl = (lang === "fr" ? t.inclusions_fr : t.inclusions_en) || t.inclusions_fr || [];
                    if (!title && !t.price_from) return null;
                    return (
                      <div key={i} className="border-t border-[#D4AF37]/40 pt-4" data-testid={`tier-${s.slug}-${i}`}>
                        <div className="flex items-baseline justify-between gap-3">
                          <div>
                            <div className="overline text-[#D4AF37]">{["Essentiel", "Signature", "Prestige"][i]}</div>
                            <h3 className="font-serif text-2xl text-[#0A1128] mt-1">{title}</h3>
                          </div>
                          {t.price_from > 0 && (
                            <div className="font-serif text-xl text-[#0A1128] whitespace-nowrap">
                              {lang === "fr" ? "dès" : "from"} <span className="text-[#D4AF37]">{t.price_from.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} {t.currency || "EUR"}</span>
                            </div>
                          )}
                        </div>
                        {tdesc && <p className="text-sm text-[#0A1128]/70 mt-2">{tdesc}</p>}
                        {incl.length > 0 && (
                          <ul className="mt-3 space-y-1.5">
                            {incl.map((it, j) => (
                              <li key={j} className="flex items-baseline gap-2 text-sm text-[#0A1128]/80">
                                <span className="text-[#D4AF37]">◆</span>
                                <span>{it}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Link to="/book" className="inline-block mt-10 border-b border-[#0A1128] pb-1 text-xs tracking-[0.24em] uppercase hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
                  {lang === "fr" ? "Prendre rendez-vous" : "Book a call"}
                </Link>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
